import React from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableRow,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { AutoAwesome, ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import AnalysisTabBar from '../components/AnalysisTabBar';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';
import { formatDisplayDate } from '../utils/dateDisplay';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const NAKSHATRAS = [
  'Ashwini',
  'Bharani',
  'Krittika',
  'Rohini',
  'Mrigashira',
  'Ardra',
  'Punarvasu',
  'Pushya',
  'Ashlesha',
  'Magha',
  'Purva Phalguni',
  'Uttara Phalguni',
  'Hasta',
  'Chitra',
  'Swati',
  'Vishakha',
  'Anuradha',
  'Jyeshtha',
  'Mula',
  'Purva Ashadha',
  'Uttara Ashadha',
  'Shravana',
  'Dhanishta',
  'Shatabhisha',
  'Purva Bhadrapada',
  'Uttara Bhadrapada',
  'Revati',
];

const pad = (value: number) => String(value).padStart(2, '0');

const toInputDateTime = (date: Date) => (
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
);

const toApiDate = (value: string, timezoneOffset: number) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) throw new Error(`Invalid local date/time: ${value}`);
  const [, year, month, day, hour, minute] = match;
  const utcMillis = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
    - timezoneOffset * 60000;
  return new Date(utcMillis).toISOString();
};
const dateOnly = (value: string) => value.slice(0, 10);
const timeOnly = (value: string) => value.slice(11, 16);
const combineDateTime = (date: string, time: string) => `${date}T${time}`;
const addDaysToDateString = (value: string, days: number) => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const timezoneToMinutes = (value: any) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      return sign * ((Number(match[2]) || 0) * 60 + (Number(match[3]) || 0));
    }
    const named = trimmed.match(/UTC\s*([+-]\d{1,2})(?::?(\d{2}))?/i);
    if (named) {
      const hours = Number(named[1]) || 0;
      const minutes = Number(named[2]) || 0;
      return (hours < 0 ? -1 : 1) * (Math.abs(hours) * 60 + minutes);
    }
  }
  return 330;
};

const timeToDateTime = (date: string, time: string) => {
  if (!time || time === 'N/A') return '';
  return combineDateTime(date, time.slice(0, 5));
};

const BIRD_ICON: Record<string, string> = {
  Peacock: '🦚',
  Vulture: '🦅',
  Owl: '🦉',
  Crow: '🐦‍⬛',
  Cock: '🐓',
};

const ACTIVITY_ICON: Record<string, string> = {
  Rule: '👑',
  Eat: '🍽️',
  Walk: '🐾',
  Sleep: '💤',
  Death: '🪦',
};

const EFFECT_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  'Very Good': 'success',
  Good: 'success',
  Average: 'warning',
  Bad: 'error',
  'Very Bad': 'error',
};

const formatTime = (value: string, timezoneOffset = 330) => new Date(new Date(value).getTime() + timezoneOffset * 60000).toLocaleTimeString('en-IN', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
});

const formatDayHeading = (value: string) => formatDisplayDate(value);

const findPlanet = (chart: any, planetName: string) => {
  const planets = chart?.planets || {};
  return Object.values(planets).find((planet: any) => String(planet?.name || '').toLowerCase() === planetName.toLowerCase())
    || planets[planetName]
    || planets[planetName.toUpperCase()]
    || planets[planetName.toLowerCase()];
};

const deriveBirthPaksha = (chart: any) => {
  const sun: any = findPlanet(chart, 'Sun');
  const moon: any = findPlanet(chart, 'Moon');
  const sunLongitude = Number(sun?.longitude);
  const moonLongitude = Number(moon?.longitude);
  if (!Number.isFinite(sunLongitude) || !Number.isFinite(moonLongitude)) return 'sukla';
  const diff = ((moonLongitude - sunLongitude + 360) % 360);
  const tithi = Math.floor(diff / 12) + 1;
  return tithi <= 15 ? 'sukla' : 'krishna';
};

const getChartNakshatra = (chart: any) => {
  const moon: any = findPlanet(chart, 'Moon');
  return moon?.nakshatra?.name || 'Uttara Bhadrapada';
};

const getChartPlace = (chart: any) => {
  const place = chart?.birthDetails?.placeOfBirth;
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return {
    latitude,
    longitude,
    timezoneOffset: timezoneToMinutes(place?.timezone),
    label: [place?.city, place?.country].filter(Boolean).join(', '),
  };
};

const PanchPakshi: React.FC = () => {
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const { id } = useParams();
  const { state: authState } = useAuth();
  const now = React.useMemo(() => new Date(), []);
  const [form, setForm] = React.useState({
    birthNakshatra: 'Uttara Bhadrapada',
    birthPaksha: 'sukla',
    currentPaksha: 'sukla',
    targetDateTime: toInputDateTime(now),
    sunrise: '',
    sunset: '',
    nextSunrise: '',
  });
  const [chartPlace, setChartPlace] = React.useState<any>(null);
  const [chartName, setChartName] = React.useState('');
  const [isHoraryChart, setIsHoraryChart] = React.useState(false);
  const [result, setResult] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [showPremiumLock, setShowPremiumLock] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true),
  });
  const selectedDate = dateOnly(form.targetDateTime);

  const fetchPanchang = React.useCallback(async (date: string) => {
    if (!chartPlace) return null;
    const params = new URLSearchParams({
      lat: String(chartPlace.latitude),
      lon: String(chartPlace.longitude),
      timezoneOffset: String(chartPlace.timezoneOffset),
      date,
    });
    const response = await axios.get(`${API_BASE_URL}/panchang?${params.toString()}`);
    return response.data?.data?.panchang;
  }, [chartPlace]);

  const getPanchangTimesForDate = React.useCallback(async (date: string) => {
    const previousDate = addDaysToDateString(date, -1);
    const nextDate = addDaysToDateString(date, 1);
    const [previousPanchang, panchang, nextPanchang] = await Promise.all([
      fetchPanchang(previousDate),
      fetchPanchang(date),
      fetchPanchang(nextDate),
    ]);
    if (!previousPanchang || !panchang || !nextPanchang) return null;

    const tithiNumber = Number(panchang.details?.tithiNumber);
    const paksha = Number.isFinite(tithiNumber) && tithiNumber > 15 ? 'krishna' : 'sukla';
    const previousTithiNumber = Number(previousPanchang.details?.tithiNumber);
    const previousPaksha = Number.isFinite(previousTithiNumber) && previousTithiNumber > 15 ? 'krishna' : 'sukla';

    return {
      currentPaksha: paksha,
      previousPaksha,
      previousSunrise: timeToDateTime(previousDate, previousPanchang.sunrise),
      previousSunset: timeToDateTime(previousDate, previousPanchang.sunset),
      sunrise: timeToDateTime(date, panchang.sunrise),
      sunset: timeToDateTime(date, panchang.sunset),
      nextSunrise: timeToDateTime(nextDate, nextPanchang.sunrise),
    };
  }, [fetchPanchang]);

  React.useEffect(() => {
    document.title = 'Panch Pakshi Calculator - Kundlistar';
  }, []);

  React.useEffect(() => {
    if (!id) return;
    let mounted = true;

    const loadChart = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/charts/${id}`);
        const chart = response.data?.data?.chart || response.data?.data || response.data;
        if (!mounted || !chart) return;

        const paksha = deriveBirthPaksha(chart);
        setChartName(chart.birthDetails?.name || '');
        setChartPlace(getChartPlace(chart));
        setIsHoraryChart(chart.chartType === 'horary');
        setForm((prev) => ({
          ...prev,
          birthNakshatra: getChartNakshatra(chart),
          birthPaksha: paksha,
          currentPaksha: paksha,
        }));
      } catch {
        if (mounted) setError('Could not prefill Panch Pakshi from chart. You can still calculate manually.');
      }
    };

    loadChart();
    return () => {
      mounted = false;
    };
  }, [id]);

  React.useEffect(() => {
    if (!chartPlace) return;
    let mounted = true;
    getPanchangTimesForDate(selectedDate)
      .then((times) => {
        if (!mounted || !times) return;
        setForm((prev) => ({
          ...prev,
          currentPaksha: times.currentPaksha,
          sunrise: times.sunrise,
          sunset: times.sunset,
          nextSunrise: times.nextSunrise,
        }));
      })
      .catch(() => {
        if (mounted) setError('Could not load sunrise and sunset from Panchang for this chart location.');
      });
    return () => {
      mounted = false;
    };
  }, [chartPlace, selectedDate, getPanchangTimesForDate]);

  const updateField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applySelectedDate = (nextDate: string) => {
    const nextSunriseDate = addDaysToDateString(nextDate, 1);
    setForm((prev) => ({
      ...prev,
      targetDateTime: combineDateTime(nextDate, timeOnly(prev.targetDateTime)),
      sunrise: combineDateTime(nextDate, timeOnly(prev.sunrise)),
      sunset: combineDateTime(nextDate, timeOnly(prev.sunset)),
      nextSunrise: combineDateTime(nextSunriseDate, timeOnly(prev.nextSunrise)),
    }));
  };

  const shiftDay = (days: number) => applySelectedDate(addDaysToDateString(selectedDate, days));
  const goToday = () => applySelectedDate(dateOnly(toInputDateTime(new Date())));

  const calculate = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);

      if (!chartPlace) {
        throw new Error('Open Panch Pakshi from a saved/calculated chart so sunrise and sunset can be loaded from birth place Panchang.');
      }

      const panchangTimes = await getPanchangTimesForDate(selectedDate);
      if (!panchangTimes?.sunrise || !panchangTimes?.sunset || !panchangTimes?.nextSunrise) {
        throw new Error('Unable to load sunrise, sunset and next sunrise from Panchang.');
      }

      const isBeforeSunrise = timeOnly(form.targetDateTime) < timeOnly(panchangTimes.sunrise);
      const calculationForm = {
        ...form,
        currentPaksha: isBeforeSunrise ? panchangTimes.previousPaksha : panchangTimes.currentPaksha,
        sunrise: panchangTimes.sunrise,
        sunset: panchangTimes.sunset,
        nextSunrise: panchangTimes.nextSunrise,
      };
      setForm(calculationForm);

      if (!authState.token) {
        window.location.assign('/login');
        return;
      }

      const payload = {
        ...calculationForm,
        localDate: selectedDate,
        timezoneOffset: chartPlace.timezoneOffset,
        targetDateTime: toApiDate(calculationForm.targetDateTime, chartPlace.timezoneOffset),
        previousSunrise: toApiDate(panchangTimes.previousSunrise, chartPlace.timezoneOffset),
        previousSunset: toApiDate(panchangTimes.previousSunset, chartPlace.timezoneOffset),
        sunrise: toApiDate(calculationForm.sunrise, chartPlace.timezoneOffset),
        sunset: toApiDate(calculationForm.sunset, chartPlace.timezoneOffset),
        nextSunrise: toApiDate(calculationForm.nextSunrise, chartPlace.timezoneOffset),
        language,
      };

      await checkFeatureAccess({
        featureKey: 'PANCH_PAKSHI',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}/panch-pakshi/calculate`, payload, {
          headers: {
            Authorization: `Bearer ${authState.token}`,
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {}),
          },
        }),
        onSuccess: (data: any) => {
          setResult(data.data);
          setShowPremiumLock(false);
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Panch Pakshi calculation failed.');
    } finally {
      setLoading(false);
    }
  };

  if (!authState.isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Helmet>
          <title>Panch Pakshi Calculator | Kundlistar</title>
          <meta
            name="description"
            content="Calculate Panch Pakshi bird, activity, Yamam, Apahara period, relationship, score and result."
          />
        </Helmet>

        {id && <AnalysisTabBar chartId={id} active="panchPakshi" isHoraryChart={isHoraryChart} />}

        <Paper sx={{ p: 3, textAlign: 'center', mt: id ? 3 : 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "var(--app-ink-primary)" }}>
            Unlock Panch Pakshi Calculator
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            Login to access Panch Pakshi analysis.
          </Typography>
          <Button variant="contained" onClick={() => window.location.assign('/login')}>
            Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Helmet>
        <title>Panch Pakshi Calculator | Kundlistar</title>
        <meta
          name="description"
          content="Calculate Panch Pakshi bird, activity, Yamam, Apahara period, relationship, score and result."
        />
      </Helmet>

      {id && <AnalysisTabBar chartId={id} active="panchPakshi" isHoraryChart={isHoraryChart} />}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "var(--app-ink-primary)" }}>
          {translateInline(language, 'Panch Pakshi Calculator')}
        </Typography>
        <Typography color="text.secondary">
          {chartName ? `${chartName} - ` : ''}{translateInline(language, 'Birth Pakshi, Yamam and Apahara result based on Nakshatra, Paksha, weekday and day/night.')}
        </Typography>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {!isPremiumUser && (
        <Alert severity={showPremiumLock ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {showPremiumLock
            ? translateInline(language, 'Your 3-day trial has ended. Choose a Kundlistar Studio plan to continue.')
            : translateInline(language, 'This studio feature is included during your 3-day trial.')}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <TextField
              type="date"
              label={translateInline(language, 'Selected Date')}
              InputLabelProps={{ shrink: true }}
              value={selectedDate}
              onChange={(event) => applySelectedDate(event.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<ChevronLeft />} onClick={() => shiftDay(-1)}>{translateInline(language, 'Prev Day')}</Button>
              <Button variant="outlined" startIcon={<Today />} onClick={goToday}>{translateInline(language, 'Today')}</Button>
              <Button variant="outlined" endIcon={<ChevronRight />} onClick={() => shiftDay(1)}>{translateInline(language, 'Next Day')}</Button>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <TextField
                select
                fullWidth
                label={translateInline(language, 'Birth Nakshatra')}
                value={form.birthNakshatra}
                onChange={(event) => updateField('birthNakshatra', event.target.value)}
              >
                {NAKSHATRAS.map((nakshatra) => (
                  <MenuItem key={nakshatra} value={nakshatra}>{nakshatra}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <TextField
                select
                fullWidth
                label={translateInline(language, 'Birth Paksha')}
                value={form.birthPaksha}
                onChange={(event) => updateField('birthPaksha', event.target.value)}
              >
                <MenuItem value="sukla">{translateInline(language, 'Sukla Paksha')}</MenuItem>
                <MenuItem value="krishna">{translateInline(language, 'Krishna Paksha')}</MenuItem>
              </TextField>
            </Box>
            <Box>
              <TextField
                select
                fullWidth
                label={translateInline(language, 'Current Paksha')}
                value={form.currentPaksha}
                onChange={(event) => updateField('currentPaksha', event.target.value)}
              >
                <MenuItem value="sukla">{translateInline(language, 'Sukla Paksha')}</MenuItem>
                <MenuItem value="krishna">{translateInline(language, 'Krishna Paksha')}</MenuItem>
              </TextField>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 2 }}>
            <Box>
              <TextField
                fullWidth
                type="datetime-local"
                label={translateInline(language, 'Target Date Time')}
                InputLabelProps={{ shrink: true }}
                value={form.targetDateTime}
                onChange={(event) => updateField('targetDateTime', event.target.value)}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                type="datetime-local"
                label={translateInline(language, 'Sunrise')}
                InputLabelProps={{ shrink: true }}
                value={form.sunrise}
                onChange={(event) => updateField('sunrise', event.target.value)}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                type="datetime-local"
                label={translateInline(language, 'Sunset')}
                InputLabelProps={{ shrink: true }}
                value={form.sunset}
                onChange={(event) => updateField('sunset', event.target.value)}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                type="datetime-local"
                label={translateInline(language, 'Next Sunrise')}
                InputLabelProps={{ shrink: true }}
                value={form.nextSunrise}
                onChange={(event) => updateField('nextSunrise', event.target.value)}
              />
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={loading || trialLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
            onClick={calculate}
            disabled={loading || trialLoading}
            sx={{ mt: 2 }}
          >
            {loading || trialLoading ? translateInline(language, 'Calculating...') : translateInline(language, 'Calculate Panch Pakshi')}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Box>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                {formatDayHeading(result.panchangDate || selectedDate)}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                {[
                  [translateInline(language, 'Birth Pakshi'), `${BIRD_ICON[result.birthPakshi] || ''} ${translateInline(language, result.birthPakshi)}`],
                  [translateInline(language, 'Paksha'), translateInline(language, result.currentPaksha)],
                  [translateInline(language, 'Weekday'), translateInline(language, result.weekday)],
                  [translateInline(language, 'Current Period'), translateInline(language, result.period)],
                  [translateInline(language, 'Current Yamam'), result.yamamNumber],
                  [translateInline(language, 'Main Activity'), `${ACTIVITY_ICON[result.mainActivity] || ''} ${translateInline(language, result.mainActivity)}`],
                  [translateInline(language, 'Ruling Bird'), `${BIRD_ICON[result.rulingBird] || ''} ${translateInline(language, result.rulingBird)}`],
                  [translateInline(language, 'Death Bird'), `${BIRD_ICON[result.deathBird] || ''} ${translateInline(language, result.deathBird)}`],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography sx={{ fontWeight: 800, textTransform: label === 'Paksha' || label === 'Current Period' ? 'capitalize' : 'none' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {result.currentApahara && (
                <Paper sx={{ p: 2, bgcolor: "var(--app-surface-neutral, #f1f8f4)" }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <Chip color="primary" label={`${translateInline(language, 'Sub Bird')}: ${BIRD_ICON[result.currentApahara.bird] || ''} ${translateInline(language, result.currentApahara.bird)}`} />
                    <Chip label={`${translateInline(language, 'Activity')}: ${ACTIVITY_ICON[result.currentApahara.activity] || ''} ${translateInline(language, result.currentApahara.activity)}`} />
                    <Chip label={`${translateInline(language, 'Relation')}: ${translateInline(language, result.currentApahara.relation)}`} />
                    <Chip color={EFFECT_COLOR[result.currentApahara.effect] || 'default'} label={translateInline(language, result.currentApahara.effect)} />
                  </Box>
                  <Typography>{result.summary}</Typography>
                </Paper>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                {translateInline(language, 'Full Panch Pakshi Schedule')}
              </Typography>
              {(result.schedule || []).map((yamam: any) => (
                <Box
                  key={`${yamam.period}-${yamam.yamamNumber}`}
                  sx={{
                    mb: 1.5,
                    border: '1px solid',
                    borderColor: yamam.period === result.period && yamam.yamamNumber === result.yamamNumber ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: yamam.period === 'day' ? "var(--app-surface-orange, #fff7e6)" : "var(--app-surface-orange, #fff1d6)",
                  }}
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr 1fr 1fr' }, gap: 1, alignItems: 'center', px: 1.5, py: 1, bgcolor: "var(--app-surface-orange, #f8ad44)" }}>
                    <Typography sx={{ fontWeight: 800 }}>
                      {formatTime(yamam.start, result.timezoneOffset)} to {formatTime(yamam.end, result.timezoneOffset)}
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      Yamam {yamam.yamamNumber} · {yamam.period}
                    </Typography>
                    <Typography>
                      {BIRD_ICON[yamam.mainBird] || ''} {yamam.mainBird}
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>
                      {ACTIVITY_ICON[yamam.mainActivity] || ''} {yamam.mainActivity}
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {yamam.allApahara.map((item: any, index: number) => (
                          <TableRow key={`${yamam.period}-${yamam.yamamNumber}-${item.bird}-${index}`} selected={item.start === result.currentApahara?.start}>
                            <TableCell sx={{ width: 190 }}>{formatTime(item.start, result.timezoneOffset)} to {formatTime(item.end, result.timezoneOffset)}</TableCell>
                            <TableCell sx={{ width: 80 }}>{item.durationMinutes} min</TableCell>
                            <TableCell>{BIRD_ICON[item.bird] || ''} {item.bird}</TableCell>
                            <TableCell>{ACTIVITY_ICON[item.activity] || ''} {item.activity}</TableCell>
                            <TableCell>{item.effect}<br /><Box component="span" sx={{ color: item.score >= 60 ? 'success.main' : item.score >= 35 ? "var(--app-ink-primary)" : 'error.main', letterSpacing: 1 }}>{item.rating}</Box><br />Power {Number(item.powerFactor).toFixed(2)}</TableCell>
                            <TableCell>{item.relation}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Notes: Timings use the chart location timezone. The Panchang weekday runs from sunrise to the following sunrise.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading || loading}
        onCancel={closePopup}
        onContinue={popup.nextAction || (() => Promise.resolve())}
      />
    </Container>
  );
};

export default PanchPakshi;
